/**
 * Full-screen loading state with rotating, reassuring messages.
 *
 * Used while a slow request is in flight (e.g. building onboarding shelves) so
 * the shopper sees that work is happening rather than a frozen screen. Messages
 * are cycled on a timer; the caller supplies already-translated copy so this
 * component stays presentation-only and bilingual-safe.
 */

import { Loader2 } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';

interface LoadingScreenProps {
  /** Already-translated messages to cycle through (non-empty). */
  messages: string[];
  /** Milliseconds each message is shown before advancing. */
  intervalMs?: number;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ messages, intervalMs = 2600 }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (messages.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % messages.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [messages.length, intervalMs]);

  const message = messages[index] ?? '';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 font-body">
      <div className="text-center max-w-md">
        <Loader2 className="w-12 h-12 text-indigo-brand-text animate-spin mx-auto mb-6" />
        <p
          key={index}
          aria-live="polite"
          className="text-lg text-indigo-brand-text/70 animate-fadeIn"
        >
          {message}
        </p>
      </div>
    </div>
  );
};

export default LoadingScreen;
